import { useState } from 'react';

import { snakeToCamel } from '@mint-lib/api';
import { useTranslation } from '@mint-lib/i18n';
import { Download as DownloadIcon } from '@mint-lib/icons';
import { useFlags } from '@mint-lib/routing-context';
import {
  Button,
  CircularLoadingButton,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';
import type { Chat } from '@mint-sdk/api-ai-assistant';
import isMobile from 'is-mobile';

import ErrorLoadingChart from '../../features/Charts/ErrorLoadingChart/ErrorLoadingChart.jsx';
import {
  BarChart,
  LinePlot,
  PieChart,
  ScatterPlot,
} from '../../features/Charts/index.js';
import { useAiHubPermissions } from '../../hooks/useAiHubPermissions.jsx';
import { useScreenshot } from '../../hooks/useScreenshot.js';
import { useStructuredDataframe } from '../../hooks/useStructuredDataframe.jsx';

const ChartComponents = {
  barchart: BarChart,
  lineplot: LinePlot,
  piechart: PieChart,
  scatterplot: ScatterPlot,
};

type AiChartProps = {
  date: string;
  graph: Chat.Graph;
  dataframe?: Chat.DataframeType;
  isAgentic?: boolean;
  chartName?: string;
};
export const AiChart = ({
  date,
  isAgentic = false,
  chartName,
  dataframe,
  graph,
}: AiChartProps) => {
  const ff = useFlags('@mint/arm', [
    'agenticAiMobile',
    'aiHubExpandedChartsDownload',
  ]);

  const { chartDataframe, hasWrongData } = useStructuredDataframe({
    dataframe: dataframe ?? [],
    graph,
  });

  const [isExport, setIsExport] = useState(false);
  const isMobileEnabled = ff.agenticAiMobile.enabled && isMobile();
  const { canDownloadChart } = useAiHubPermissions();

  const isDownloadEnabled = !(isMobileEnabled || isAgentic);
  const hasHeader = isDownloadEnabled || chartName;

  const safeGraph = (graph?: Chat.Graph) => {
    const isXValid =
      graph?.instructions?.x !== undefined &&
      typeof graph?.instructions?.x === 'string';

    const isYValid =
      graph?.instructions?.y !== undefined &&
      Array.isArray(graph?.instructions?.y) &&
      graph?.instructions?.y.every((y) => typeof y === 'string');

    return {
      hasCorruptedData: !isXValid || !isYValid,
      graphType: graph?.graphType ?? null,
      instructions: {
        x: isXValid ? graph?.instructions?.x : '',
        y: isYValid ? graph?.instructions?.y : [''],
        label: snakeToCamel(graph?.instructions?.label ?? ''),
      },
    };
  };

  const { hasCorruptedData, graphType, instructions } = safeGraph(graph);
  const { x, y, label } = instructions;
  const formattedX = snakeToCamel(x);
  const formattedYs = y.map(snakeToCamel);
  const fileName = graphType ?? '';

  const { onExportPNG, captureElementRef } = useScreenshot(fileName, {
    chatDate: date,
  });

  const expandedScreenshot = async () => {
    setIsExport(true);

    try {
      await onExportPNG();
    } finally {
      setIsExport(false);
    }
  };

  const { t } = useTranslation('@mint-lib/ai-chat');

  const ChartComponent = graphType && ChartComponents[graphType];

  if (hasCorruptedData || hasWrongData || !ChartComponent) {
    return <ErrorLoadingChart errorStatus={404} />;
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-4',
        isMobileEnabled ? 'rounded-[20px] bg-ui-primary px-4 py-3' : 'p-0',
      )}
    >
      {hasHeader ? (
        <div className="flex flex-row">
          {!!chartName && (
            <div className="flex flex-1 justify-start">
              <p
                className={typographyVariants({
                  variant: 'h4',
                })}
              >
                {chartName}
              </p>
            </div>
          )}
          {isDownloadEnabled && (
            <div className="flex flex-1 justify-end">
              <Button
                onClick={expandedScreenshot}
                size="small"
                variant="outlined"
                className="self-end"
                disabled={isExport || !canDownloadChart}
              >
                {isExport ? (
                  <>
                    <CircularLoadingButton variant="outlined" size="small" />
                    {t('Downloading')}
                  </>
                ) : (
                  <>
                    <DownloadIcon />
                    {t('Download')}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      ) : null}

      <div className="h-full w-full overflow-auto" ref={captureElementRef}>
        <ChartComponent
          isAgentic={isAgentic}
          isMobile={isMobileEnabled}
          x={formattedX}
          label={label}
          y={formattedYs}
          dataframe={chartDataframe}
          isExport={isExport}
        />
      </div>
    </div>
  );
};
